import { useLocale } from '@ecomm/data-hooks'
import type { Locale } from '@simplisafe/ewok'
import { toZonedTime } from 'date-fns-tz'
import { useCallback, useEffect, useMemo, useState } from 'react'

function getAreExpertsAvailable({
  from,
  to,
  locale,
  days
}: {
  readonly from: number
  readonly to: number
  readonly locale: Locale
  /**
   * Days of the week when experts are available
   * 0 = Sunday, 1 = Monday, ..., 6 = Saturday
   */
  readonly days: readonly number[]
}) {
  const timeZone = locale === 'en-GB' ? 'Europe/London' : 'America/New_York'
  const zonedDate = toZonedTime(new Date(), timeZone)

  const currentHour = zonedDate.getHours()
  const currentDay = zonedDate.getDay()

  const isWithinHours = currentHour >= from && currentHour < to
  const isWithinDays = days.includes(currentDay)

  return isWithinHours && isWithinDays
}

/**
 * Returns whether experts are currently available via phone and chat according to a fixed schedule
 * (default: every day from 8:00am - Midnight ET). Updates every second.
 */
export function useAreExpertsAvailable(
  from = 8,
  to = 24,
  days: readonly number[] = [0, 1, 2, 3, 4, 5, 6]
) {
  const locale = useLocale()

  const daysKey = useMemo(
    () => [...days].sort((a, b) => a - b).join(','),
    [days]
  )

  const stableDays = useMemo(
    () => (daysKey ? daysKey.split(',').map(Number) : []),
    [daysKey]
  )

  const compute = useCallback(
    () => getAreExpertsAvailable({ from, to, locale, days: stableDays }),
    [from, to, locale, stableDays]
  )

  const [areExpertsAvailable, setAreExpertsAvailable] = useState(compute)

  useEffect(() => {
    // refresh immediately on dep change
    setAreExpertsAvailable(compute())

    const interval = setInterval(() => {
      setAreExpertsAvailable(compute())
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [compute])

  return areExpertsAvailable
}
